import { zodResolver } from '@hookform/resolvers/zod';
import { Loader } from "lucide-react";
import { useEffect } from 'react';
import { useForm } from "react-hook-form";
import { useNavigate } from 'react-router';
import { z } from 'zod';

import { BorderBox } from '@/components/atoms/BorderBox';
import Combobox from "@/components/custom/Combobox";
import ErrorPopup from "@/components/custom/InfoPopups/ErrorPopup";
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SelectCrmSystemSanitizedCombobox } from '@/features/crm_system/components/molecules/SelectCrmSystemSanitizedCombobox';
import { DEFAULT_CRM_SYSTEM } from '@/features/crm_system/lib/_core/crmSystem/crmSystem.constants.gen';
import { CrmSystemIds } from '@/features/crm_system/lib/_core/crmSystem/crmSystem.types.gen';
import { useCopySurveyToProject } from '@/features/surveys/hooks/useCopySurveyToProject';
import { crmSystemSchema } from '@/lib/schema/crm/crm_system';
import { Tables, TablesInsert } from '@/lib/supabase/supabase_types_override';
import { useGetProjectsByOrgIdAndCrmSystem } from '@/lib/useQuery/project/useGetProjectsByOrgIdAndCrmSystem';
import { useGetSurveyByIdReturnData } from '@/lib/useQuery/survey/useGetSurveyById';
import { useOrganizationStore, useUserStore } from '@/lib/zustand/useStore';


interface Props<T extends Tables<'project'> = Tables<'project'>> {
    show: boolean,
    onHide: () => void,
    title?: string,
    description?: string,

    orgId: number,
    crmSystem: string,

    copiedSurvey?: useGetSurveyByIdReturnData & { crm_system?: CrmSystemIds } | null,
    questions?: Tables<'question'>[] | null,
    copiedFromProject?: T | null,
    copyFromBank?: boolean,
}

export const CopySurveyDialog = <T extends Tables<'project'> = Tables<'project'>>({
    show,
    onHide,
    title = "Create survey from template",
    description = "Select a project to which you want to copy this survey template.",
    orgId,
    copiedSurvey,
    copiedFromProject,
    copyFromBank = false,
}: Props<T>) => {
    const navigate = useNavigate();
    const userData = useUserStore(state => state);
    const {organization} = useOrganizationStore();
    const surveySchema = z.object({
        name: z.string({ message: "Field is required" }),
        description: z.string().optional(),
        project_id: z.string({ message: "Field is required" }),
        crm_system: crmSystemSchema
    } satisfies Record<
        keyof Pick<
            TablesInsert<'survey'>,
            'name' | 'description' | 'project_id'>,
        unknown
    > & {
        crm_system: unknown
    });
    type surveySchemaType = z.infer<typeof surveySchema>;
    const form = useForm<surveySchemaType>({
        resolver: zodResolver(surveySchema)
    });
    const pickedCrmSystem = form.watch('crm_system');
    const originalCrmSystem = copiedSurvey?.crm_system ?? copiedSurvey?.crm?.crm_system ?? copiedFromProject?.crm_system ?? DEFAULT_CRM_SYSTEM;
    useEffect(() => {
        if (show) {
            form.reset({
                name: copiedSurvey?.name || '',
                description: copiedSurvey?.description || '',
                crm_system: originalCrmSystem,
            });
        }
    }, [form, show, copiedSurvey?.description, copiedSurvey?.name, copiedSurvey?.crm?.crm_system, copiedSurvey?.crm, originalCrmSystem]);

    const onCloseCreateSurveyModal = () => {
        onHide();
        form.reset({
            name: '',
            description: ''
        });
    }
    const { mutateAsync: copySurveyToProject, isPending: isPendingCopySurvey } = useCopySurveyToProject()
    const { data: projects, isFetching: isFetchingProjects } = useGetProjectsByOrgIdAndCrmSystem({
        orgId: orgId,
        crmSystem: pickedCrmSystem,
        isOpened: show
    });
    const onSubmitSurvey = async ({
        name,
        description,
        projectId,
        userId,
        organisationId,
        crmId,
    }: {
        name: string;
        projectId: string;
        organisationId: number;
        description?: string | undefined;
        userId: string;
        crmId?: number;
    }) => {
        if (!projectId || !userId || !organisationId) throw new Error('Project ID, User ID and Organisation ID are required to create a survey.');

        const requestBody = {
            project_id: projectId,
            crm_id: crmId,
            description,
            name,
            author_id: userId,
        }

        try {

            const { data: surveyData } = await copySurveyToProject({
                copyFromBank: copyFromBank,
                id: copiedSurvey?.id || '',
                ...requestBody,
            });

            onCloseCreateSurveyModal();
            SuccessPopup('Survey added successfully!');
            navigate(`/organization/${organisationId}/project/${projectId}/survey/${surveyData.id}/manage-survey`);

        } catch (error) {
            if (error instanceof Error) {
                ErrorPopup(error.message);
            } else {
                ErrorPopup('Error adding survey. Please try again later.');
            }
        }
    }

    const onSubmitCreateSurvey = (data: surveySchemaType) => {
        const crmId = projects?.find(project => project.id === data.project_id)?.crm_id || undefined;
        const organisationId = orgId ? Number(orgId) : 0;
        const userId = userData?.id || '';

        onSubmitSurvey({
            name: data.name,
            description: data.description,
            projectId: data.project_id,
            userId,
            organisationId,
            crmId,
        });
    }

    return (
        <Dialog open={show} onOpenChange={onCloseCreateSurveyModal}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {title}
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        {description}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form className="px-1 py-1 space-y-2">
                        <FormField
                            control={form.control}
                            name='crm_system'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Select CRM System</FormLabel>
                                    <FormControl>
                                        <SelectCrmSystemSanitizedCombobox
                                            mode='single'
                                            selectedValue={field.value || originalCrmSystem}
                                            setSelectedValue={field.onChange}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {(() => {
                            if ((organization?.crm_connections?.length ?? 0) < 2) {
                                return null
                            }
                            if (originalCrmSystem === pickedCrmSystem) {
                                return (
                                    <BorderBox variant='info' size='sm'>
                                        Select a CRM system for project to copy survey to. It's best to use original CRM System to preserve all fields functionality. The system will be used to convert the
                                        survey questions to the new system's field types.
                                    </BorderBox>
                                )
                            } else {
                                return (
                                    <BorderBox variant='danger' size='sm'>
                                        Some questions may not be compatible with the selected CRM system. Please be aware that some fields may not work as expected.
                                    </BorderBox>
                                )
                            }
                        })()}
                        <FormField
                            control={form.control}
                            name='project_id'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Select Project</FormLabel>
                                    <FormControl>
                                        <Combobox
                                            disabled={isFetchingProjects}
                                            inDialog
                                            mode="single"
                                            items={[{ label: "Clear selection", value: "" }, ...(projects || []).map((project) => ({
                                                label: project.name || "No name",
                                                value: project.id
                                            }))]}
                                            setSelectedValue={field.onChange}
                                            selectedValue={field.value ? String(field.value) : null}
                                            triggerLabel={isFetchingProjects ? <Loader size="16" className="animate-spin" /> : "Select Project"}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <BorderBox variant='info' size='sm'>
                            You can adjust a name and description for the new survey.
                        </BorderBox>
                        <FormField
                            control={form.control}
                            name='name'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Survey Name</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='description'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Survey Description</FormLabel>
                                    <FormControl>
                                        <Textarea {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </form>
                </Form>

                <DialogFooter>
                    <div className="flex items-stretch justify-end m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                        <Button size="sm" variant="secondary" onClick={onCloseCreateSurveyModal} disabled={isPendingCopySurvey}>
                            Cancel
                        </Button>
                        <Button size="sm" onClick={form.handleSubmit(onSubmitCreateSurvey)} disabled={isPendingCopySurvey}>
                            {(isPendingCopySurvey) && (<Loader size="16" className="animate-spin" />)}
                            Submit
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
